import { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { DocumentListTable } from "./DocumentListTable";
import { DocumentFiltersReset } from "../../context/DocumentFiltersContext";
import { fetchChildDocuments } from "@/stores/documents/repository/queries";
import type { DocumentStoreInstance } from "@/stores/documents/DocumentStore";

/** Category with id and name */
interface CategoryTag {
  id: number;
  name: string;
  name_et?: string | null;
}

/** Design discipline from junction table */
interface DesignDisciplineTag {
  id: number;
  name: string;
  name_en?: string | null;
  abbreviation: string | null;
  description?: string | null;
}

/** Design stage from junction table */
interface DesignStageTag {
  id: number;
  name: string;
  name_en?: string | null;
  abbreviation: string | null;
}

/** Document with categories (matching DocumentListTable's expected type) */
interface DocumentWithTags {
  id: string;
  filename: string;
  file_path: string;
  building_id: string;
  content_type: string;
  size: number;
  created_at: string;
  original_created_at?: string | null;
  created_by?: string | number | null;
  created_by_name: string | null;
  created_by_avatar_url?: string | null;
  created_by_color?: string | null;
  verified_at?: string | null;
  verified_by_name?: string | null;
  state: string | null;
  version: number | null;
  version_group: string | null;
  parent_document_id: string | null;
  folder_id?: string | null;
  project_id?: number | null;
  categories?: CategoryTag[];
  design_disciplines?: DesignDisciplineTag[];
  design_stages?: DesignStageTag[];
}

interface ChildDocumentsDialogProps {
  /** Parent document ID - null means closed */
  parentDocumentId: string | null;
  /** Parent document filename for display */
  parentFilename: string;
  /** Building ID for DocumentListTable */
  buildingId: string;
  /** Document store instance */
  store: DocumentStoreInstance;
  /** Callback when dialog opens/closes */
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal dialog showing child documents of a container file (.asice, .zip).
 * Uses DocumentListTable with disableChildModal=true to prevent nested modals.
 */
export const ChildDocumentsDialog = observer(function ChildDocumentsDialog({
  parentDocumentId,
  parentFilename,
  buildingId,
  store,
  onOpenChange,
}: ChildDocumentsDialogProps) {
  const [children, setChildren] = useState<DocumentWithTags[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = parentDocumentId !== null;

  const loadChildren = useCallback(async () => {
    if (!parentDocumentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchChildDocuments(parentDocumentId);

      // Map to the format expected by DocumentListTable
      const mappedDocs: DocumentWithTags[] = data.map((doc) => ({
        id: String(doc.id),
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: String(doc.building_id),
        content_type: doc.content_type || "",
        size: doc.size || 0,
        created_at: doc.created_at,
        original_created_at: doc.original_created_at,
        modified_at: doc.modified_at,
        document_content_modified_date: doc.document_content_modified_date,
        created_by: doc.created_by,
        created_by_name: doc.created_by_name,
        created_by_avatar_url: doc.created_by_avatar_url,
        created_by_color: doc.created_by_color,
        verified_at: doc.verified_at ?? null,
        verified_by_name: doc.verified_by_name ?? null,
        state: doc.state,
        version: doc.version,
        version_group: doc.version_group,
        parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
        folder_id: doc.folder_id ? String(doc.folder_id) : null,
        project_id: doc.project_id,
        categories: doc.categories,
        design_disciplines: doc.design_disciplines,
        design_stages: doc.design_stages,
      }));

      setChildren(mappedDocs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load child documents");
    } finally {
      setIsLoading(false);
    }
  }, [parentDocumentId]);

  // Load children when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadChildren();
    } else {
      // Reset state when dialog closes
      setChildren([]);
      setError(null);
    }
  }, [isOpen, loadChildren]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="truncate">Files in: {parentFilename}</DialogTitle>
        </DialogHeader>

        <div className="min-h-[300px] flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={loadChildren}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          ) : children.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">No files found in this container</p>
            </div>
          ) : (
            <div className="h-full overflow-auto">
              {/* This table lists ONE container's contents, so it must not see
                  the page's filters — sorting it (or, before that box was
                  removed, searching in it) otherwise rewrote the shared state
                  and silently refetched the document list behind the dialog. */}
              <DocumentFiltersReset>
                <DocumentListTable
                  buildingId={buildingId}
                  store={store}
                  documents={children}
                  showCheckbox={false}
                  showVersion={true}
                  showDateCreated={true}
                  showStatus={true}
                  showUser={false}
                  showTags={true}
                  showParent={false}
                  showActions={true}
                  showDownloadAction={true}
                  disableChildModal={true}
                />
              </DocumentFiltersReset>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});
